import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

export default function AdminLogsPage() {
  const [files, setFiles] = useState([]);
  const [logDir, setLogDir] = useState("");
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState("");
  const [linesCount, setLinesCount] = useState(500);

  async function loadFiles() {
    try {
      const r = await api("/admin/logs");
      setFiles(r.files);
      setLogDir(r.log_dir);
      if (!selected && r.files.length > 0) {
        setSelected(r.files[0].name);
      }
    } catch (err) {
      console.error("logs list:", err);
    }
  }

  async function loadFile(name) {
    try {
      const r = await api(`/admin/logs/tail?file=${encodeURIComponent(name)}&lines=${linesCount}`);
      setContent(r.lines || []);
    } catch (err) {
      console.error("log file:", err);
    }
  }

  useEffect(() => { loadFiles(); }, []);

  useEffect(() => {
    if (!selected) return;
    loadFile(selected);
    if (!autoRefresh) return;
    const id = setInterval(() => loadFile(selected), 3000);
    return () => clearInterval(id);
  }, [selected, linesCount, autoRefresh]);

  const filtered = filter
    ? content.filter((l) => l.toLowerCase().includes(filter.toLowerCase()))
    : content;

  function colorForLine(line) {
    if (line.includes("[ERROR]")) return "var(--err)";
    if (line.includes("[WARN]"))  return "var(--warn)";
    if (line.includes("[INFO]"))  return "var(--text)";
    if (line.includes("[DEBUG]")) return "var(--text-dim)";
    return "var(--text-mute)";
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="eyebrow">System</div>
          <h1 className="h1">Application Logs</h1>
        </div>
        <div className="ph-meta">
          <div>{files.length} log files</div>
          <div className="mono" style={{ fontSize: 10, marginTop: 4 }}>
            {logDir}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
        {/* File list */}
        <div style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: 12,
          maxHeight: "70vh",
          overflowY: "auto",
        }}>
          {files.map((f) => (
            <div
              key={f.name}
              onClick={() => setSelected(f.name)}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderRadius: "var(--r)",
                background: selected === f.name ? "var(--bg-elev-2)" : "transparent",
                marginBottom: 4,
                borderLeft: selected === f.name ? "2px solid var(--acc)" : "2px solid transparent",
              }}
            >
              <div className="mono" style={{ fontSize: 11, fontWeight: 600 }}>{f.name}</div>
              <div className="mono" style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
                {(f.size / 1024).toFixed(1)} KB · {new Date(f.mtime).toLocaleString()}
              </div>
            </div>
          ))}
          {files.length === 0 && (
            <div style={{ padding: 20, color: "var(--text-dim)", fontSize: 12, textAlign: "center" }}>
              No log files yet
            </div>
          )}
        </div>

        {/* Log content */}
        <div style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Toolbar */}
          <div style={{
            padding: "10px 16px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}>
            <input
              type="text"
              placeholder="Filter lines…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <select value={linesCount} onChange={(e) => setLinesCount(Number(e.target.value))}>
              <option value={100}>Last 100</option>
              <option value={500}>Last 500</option>
              <option value={1000}>Last 1000</option>
              <option value={5000}>Last 5000</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} style={{ width: "auto" }} />
              Auto-refresh
            </label>
            <button className="btn ghost" onClick={() => loadFile(selected)} style={{ padding: "6px 12px", fontSize: 11 }}>
              Reload
            </button>
          </div>

          {/* Log viewer */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: 12,
            fontFamily: "var(--mono)",
            fontSize: 11,
            lineHeight: 1.5,
            maxHeight: "65vh",
            background: "var(--bg)",
          }}>
            {filtered.length === 0 ? (
              <div style={{ color: "var(--text-dim)", textAlign: "center", padding: 40 }}>
                {selected ? "No matching lines" : "Select a log file"}
              </div>
            ) : (
              filtered.map((line, i) => (
                <div key={i} style={{
                  color: colorForLine(line),
                  padding: "1px 0",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}>
                  {line}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
